/**
 * Store Action Atoms - Write operations for store
 *
 * These atoms handle all store operations (fetch listings, purchase, etc.).
 * Uses validated fetch with Zod schemas for runtime type safety.
 */

import { atom } from "jotai";
import {
  _storeListingsAtom,
  _fullListingDetailsAtom,
  storeListingsStatusAtom,
  storeListingsErrorAtom,
  storeFiltersAtom,
  storePaginationAtom,
  currentListingIdAtom,
  currentListingStatusAtom,
  currentListingErrorAtom,
  _myLicensesAtom,
  myLicensesStatusAtom,
  myLicensesErrorAtom,
  _availableUpdatesAtom,
  availableUpdatesStatusAtom,
  availableUpdatesErrorAtom,
} from "../core/store";
import {
  _myPublishableResourcesAtom,
  myPublishableResourcesStatusAtom,
  myPublishableResourcesErrorAtom,
} from "../core/my-resources";
import { accessTokenAtom } from "../core/user";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import {
  GetListingsResponseSchema,
  GetListingResponseSchema,
  GetMyLicensesResponseSchema,
  GetAvailableUpdatesResponseSchema,
  PurchaseLicenseResponseSchema,
  ImportVersionResponseSchema,
  StoreListingSchema,
  type CreateStoreListing,
  type CreateStoreReview,
  type PurchaseLicenseRequest,
  type ImportVersionRequest,
} from "@/isomorphic/schemas/store";
import { GetMyPublishableResourcesResponseSchema } from "@/isomorphic/schemas/my-resources";
import { fetchValidated, postValidated } from "@/frontend/lib/validation";

// ============================================================================
// FETCH STORE LISTINGS
// ============================================================================

/**
 * Fetch store listings with filters
 */
export const fetchStoreListingsAtom = atom(null, async (get, set) => {
  const filters = get(storeFiltersAtom);
  const pagination = get(storePaginationAtom);

  set(storeListingsStatusAtom, "loading");
  set(storeListingsErrorAtom, null);

  const searchParams = new URLSearchParams();

  if (filters.resourceType !== "all") searchParams.append("resourceType", filters.resourceType);
  if (filters.specialty) searchParams.append("specialty", filters.specialty);
  if (filters.usageContext) searchParams.append("usageContext", filters.usageContext);
  if (filters.operations.length) searchParams.append("operations", filters.operations.join(","));
  if (filters.patientTypes.length) searchParams.append("patientTypes", filters.patientTypes.join(","));
  if (filters.tags.length) searchParams.append("tags", filters.tags.join(","));
  if (filters.priceMin !== 0) searchParams.append("priceMin", filters.priceMin.toString());
  if (filters.priceMax !== 200) searchParams.append("priceMax", filters.priceMax.toString());
  if (filters.search) searchParams.append("search", filters.search);
  searchParams.append("sortBy", filters.sortBy);
  searchParams.append("page", pagination.currentPage.toString());
  searchParams.append("limit", pagination.limit.toString());

  const url = `${API_ROUTES.store.listings.getAll}?${searchParams.toString()}`;

  const result = await fetchValidated(GetListingsResponseSchema, url, {
    context: "Store listings",
  });

  if (result.success) {
    set(_storeListingsAtom, result.data.listings);
    set(storePaginationAtom, {
      ...pagination,
      totalCount: result.data.totalCount,
    });
    set(storeListingsStatusAtom, "success");
    console.log("[StoreActions] Listings loaded:", result.data.listings.length);
  } else {
    console.error("[StoreActions] Error loading listings:", result.error);
    set(storeListingsStatusAtom, "error");
    set(storeListingsErrorAtom, result.error);
  }
});

// ============================================================================
// FETCH LISTING BY ID
// ============================================================================

/**
 * Fetch a single listing by ID
 */
export const fetchListingByIdAtom = atom(null, async (get, set, listingId: string) => {
  set(currentListingStatusAtom, "loading");
  set(currentListingErrorAtom, null);

  const result = await fetchValidated(GetListingResponseSchema, API_ROUTES.store.listings.getById(listingId), {
    context: "Store listing",
  });

  if (result.success) {
    set(currentListingIdAtom, listingId);

    // Add to listings cache
    const currentListings = get(_storeListingsAtom);
    const existingIndex = currentListings.findIndex((l) => l.id === listingId);
    if (existingIndex >= 0) {
      const updated = [...currentListings];
      updated[existingIndex] = result.data.listing;
      set(_storeListingsAtom, updated);
    } else {
      set(_storeListingsAtom, [...currentListings, result.data.listing]);
    }

    // Store full details (with resource and reviews)
    const fullDetails = get(_fullListingDetailsAtom);
    set(_fullListingDetailsAtom, {
      ...fullDetails,
      [listingId]: result.data,
    });

    set(currentListingStatusAtom, "success");
    console.log("[StoreActions] Listing loaded with full details:", listingId);
  } else {
    console.error("[StoreActions] Error loading listing:", result.error);
    set(currentListingStatusAtom, "error");
    set(currentListingErrorAtom, result.error);
  }
});

// ============================================================================
// PUBLISH RESOURCE
// ============================================================================

/**
 * Publish a resource to the store
 */
export const publishResourceAtom = atom(null, async (get, set, args: { jwt: string; data: CreateStoreListing }) => {
  const { jwt, data } = args;

  if (!jwt) {
    console.error("[StoreActions] No JWT provided");
    return { success: false, error: "Not authenticated" };
  }

  const result = await postValidated(StoreListingSchema, API_ROUTES.store.publish, data, {
    jwt,
    context: "Publish resource",
  });

  if (result.success) {
    console.log("[StoreActions] Resource published:", result.data.id);
    return { success: true, data: result.data };
  } else {
    console.error("[StoreActions] Error publishing resource:", result.error);
    return { success: false, error: result.error };
  }
});

// ============================================================================
// PURCHASE LICENSE
// ============================================================================

/**
 * Purchase a license (or get free resource)
 * Returns Stripe URL for paid resources, or activates license directly for free resources
 */
export const purchaseLicenseAtom = atom(null, async (get, set, params: PurchaseLicenseRequest & { price: number }) => {
  const accessToken = get(accessTokenAtom);
  if (!accessToken) {
    console.error("[StoreActions] No access token available");
    return { success: false, error: "Not authenticated" };
  }

  const { price, ...data } = params;

  // For free resources (price = 0), skip Stripe and activate directly
  if (price === 0) {
    // Call backend to activate free license
    const result = await postValidated(PurchaseLicenseResponseSchema, API_ROUTES.store.licenses.purchase, data, {
      jwt: accessToken,
      context: "Activate free license",
    });

    if (result.success) {
      console.log("[StoreActions] Free license activated");
      // Refresh licenses
      set(fetchMyLicensesAtom);
      return { success: true, isFree: true };
    } else {
      console.error("[StoreActions] Error activating free license:", result.error);
      return { success: false, error: result.error };
    }
  }

  // For paid resources, the backend either creates a real checkout later or simulates activation in local dev
  const result = await postValidated(PurchaseLicenseResponseSchema, API_ROUTES.store.licenses.purchase, data, {
    jwt: accessToken,
    context: "Purchase license",
  });

  if (result.success) {
    console.log("[StoreActions] Paid purchase processed:", result.data.sessionId);
    set(fetchMyLicensesAtom);
    return { success: true, isFree: false, url: result.data.url };
  } else {
    console.error("[StoreActions] Error purchasing license:", result.error);
    return { success: false, error: result.error };
  }
});

// ============================================================================
// FETCH MY LICENSES
// ============================================================================

/**
 * Fetch my licenses
 */
export const fetchMyLicensesAtom = atom(null, async (get, set) => {
  const accessToken = get(accessTokenAtom);
  if (!accessToken) {
    console.error("[StoreActions] No access token available");
    return;
  }

  set(myLicensesStatusAtom, "loading");
  set(myLicensesErrorAtom, null);

  const result = await fetchValidated(GetMyLicensesResponseSchema, API_ROUTES.store.licenses.myLicenses, {
    jwt: accessToken,
    context: "My licenses",
  });

  if (result.success) {
    set(_myLicensesAtom, result.data.licenses);
    set(myLicensesStatusAtom, "success");
    console.log("[StoreActions] Licenses loaded:", result.data.licenses.length);
  } else {
    console.error("[StoreActions] Error loading licenses:", result.error);
    set(myLicensesStatusAtom, "error");
    set(myLicensesErrorAtom, result.error);
  }
});

// ============================================================================
// FETCH AVAILABLE UPDATES
// ============================================================================

/**
 * Fetch available updates for my licenses
 */
export const fetchAvailableUpdatesAtom = atom(null, async (get, set, workspaceId?: string) => {
  const accessToken = get(accessTokenAtom);
  if (!accessToken) {
    console.error("[StoreActions] No access token available");
    return;
  }

  set(availableUpdatesStatusAtom, "loading");
  set(availableUpdatesErrorAtom, null);

  const url = workspaceId
    ? `${API_ROUTES.store.buyer.updates}?workspaceId=${workspaceId}`
    : API_ROUTES.store.buyer.updates;

  const result = await fetchValidated(GetAvailableUpdatesResponseSchema, url, {
    jwt: accessToken,
    context: "Available updates",
  });

  if (result.success) {
    set(_availableUpdatesAtom, result.data.updates);
    set(availableUpdatesStatusAtom, "success");
    console.log("[StoreActions] Updates loaded:", result.data.updates.length);
  } else {
    console.error("[StoreActions] Error loading updates:", result.error);
    set(availableUpdatesStatusAtom, "error");
    set(availableUpdatesErrorAtom, result.error);
  }
});

// ============================================================================
// IMPORT NEW VERSION
// ============================================================================

/**
 * Import a new version of a licensed resource
 */
export const importNewVersionAtom = atom(
  null,
  async (get, set, params: { licenseId: string; data: ImportVersionRequest }) => {
    const accessToken = get(accessTokenAtom);
    if (!accessToken) {
      console.error("[StoreActions] No access token available");
      return { success: false, error: "Not authenticated" };
    }

    const { licenseId, data } = params;

    const result = await postValidated(
      ImportVersionResponseSchema,
      API_ROUTES.store.licenses.importVersion(licenseId),
      data,
      { jwt: accessToken, context: "Import version" },
    );

    if (result.success) {
      console.log("[StoreActions] Version imported:", result.data.importedResourceId);
      // Refresh updates
      set(fetchAvailableUpdatesAtom);
      return { success: true, data: result.data };
    } else {
      console.error("[StoreActions] Error importing version:", result.error);
      return { success: false, error: result.error };
    }
  },
);

// ============================================================================
// CREATE REVIEW
// ============================================================================

/**
 * Create a review for a purchased resource
 */
export const createReviewAtom = atom(null, async (get, set, data: CreateStoreReview) => {
  const accessToken = get(accessTokenAtom);
  if (!accessToken) {
    console.error("[StoreActions] No access token available");
    return { success: false, error: "Not authenticated" };
  }

  const result = await postValidated(StoreListingSchema, API_ROUTES.store.reviews.create, data, {
    jwt: accessToken,
    context: "Create review",
  });

  if (result.success) {
    console.log("[StoreActions] Review created");
    return { success: true };
  } else {
    console.error("[StoreActions] Error creating review:", result.error);
    return { success: false, error: result.error };
  }
});

// ============================================================================
// FETCH MY PUBLISHABLE RESOURCES
// ============================================================================

/**
 * Update a specific publishable resource after publication
 * This updates the resource's publication status without refetching all resources
 */
export const updatePublishedResourceAtom = atom(null, (get, set, args: { groupId: string; newStatus: string }) => {
  const { groupId, newStatus } = args;
  const currentResources = get(_myPublishableResourcesAtom);

  // Update all versions of this resource group with the new publication status
  const updatedResources = currentResources.map((resource) => {
    if (resource.groupId === groupId) {
      return {
        ...resource,
        publicationStatus: newStatus as "not_published" | "pending_review" | "approved" | "rejected" | "suspended",
      };
    }
    return resource;
  });

  set(_myPublishableResourcesAtom, updatedResources);
  console.log("[StoreActions] Updated resource publication status:", { groupId, newStatus });
});

/**
 * Fetch my publishable resources (agents and KBs ready to publish)
 */
export const fetchMyPublishableResourcesAtom = atom(
  null,
  async (get, set, args: { jwt: string; resourceType?: "all" | "agent" | "knowledge_base" }) => {
    const { jwt, resourceType = "all" } = args;

    if (!jwt) {
      console.error("[StoreActions] No JWT provided");
      return;
    }

    set(myPublishableResourcesStatusAtom, "loading");
    set(myPublishableResourcesErrorAtom, null);

    console.log("[StoreActions] Fetching publishable resources with resourceType:", resourceType);

    const searchParams = new URLSearchParams();
    if (resourceType && resourceType !== "all") {
      searchParams.append("resourceType", resourceType);
    }

    const url =
      resourceType !== "all"
        ? `${API_ROUTES.store.myResources}?${searchParams.toString()}`
        : API_ROUTES.store.myResources;

    const result = await fetchValidated(GetMyPublishableResourcesResponseSchema, url, {
      jwt,
      context: "My publishable resources",
    });

    if (result.success) {
      set(_myPublishableResourcesAtom, result.data.resources);
      set(myPublishableResourcesStatusAtom, "success");
      console.log("[StoreActions] Publishable resources loaded:", result.data.resources.length);
      console.log("[StoreActions] Resources data:", result.data.resources);
    } else {
      console.error("[StoreActions] Error loading publishable resources:", result.error);
      set(myPublishableResourcesStatusAtom, "error");
      set(myPublishableResourcesErrorAtom, result.error);
    }
  },
);
